import Debug from 'debug';
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import {
  colors,
  book,
  heavy,
  medium,
  nameToHex,
  mediumInter,
  lightInter,
  semiBold,
} from '@/styles';
import { Help } from '@/svgs';

// eslint-disable-next-line new-cap,no-unused-vars
const debug = Debug('modulelibrary:helptooltip');

function handleTooltipStatus(setStatus, status) {
  return (event) => {
    setStatus({
      visible: status,
      interacted: true,
    });
  };
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * Provide contextual help for customers
 *
 * @version 1.1.0
 * @param {import('.').HelpTooltipProps}
 */
function HelpTooltip({
  className,
  color,
  description,
  children,
  helpLinkText,
  helpLinkUrl,
  iconSize,
  iconColor,
  id,
  onTooltipLinkClick,
  onTooltipVisible,
  title,
  width,
  position,
  isRebrand,
}) {
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    interacted: false,
  });
  const previousState = usePrevious(tooltipState);

  useEffect(() => {
    // fire the tooltip visible handler if the state has changed.
    if (previousState && previousState.visible !== tooltipState.visible) {
      onTooltipVisible(tooltipState.visible);
    }
  }, [tooltipState.visible, previousState, onTooltipVisible]);

  /**
   * Handle a link click in the tooltip
   */
  function onTooltipLinkClicked(event) {
    onTooltipLinkClick({ event, type: 'open_url', url: helpLinkUrl });
  }

  return (
    <button
      type="button"
      className={className}
      onMouseEnter={handleTooltipStatus(setTooltipState, true)}
      onMouseLeave={handleTooltipStatus(setTooltipState, false)}
      onFocus={handleTooltipStatus(setTooltipState, true)}
      onBlur={handleTooltipStatus(setTooltipState, false)}
      aria-describedby={id}
    >
      <div
        id={id}
        className={`tooltip tooltip--${tooltipState.visible ? 'visible' : 'hidden'} tooltip--${
          position || 'above'
        } tooltip--${color}`}
        role="tooltip"
        data-testid="tooltip"
      >
        {title && <h1>{title}</h1>}
        {description && <p>{description}</p>}
        {children && <div>{children}</div>}
        {helpLinkUrl && (
          <a
            className="help"
            href={helpLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onTooltipLinkClicked}
          >
            {helpLinkText ?? 'Learn More'}
          </a>
        )}
      </div>
      {/* this div is necessary for styling to keep aspect ratio in various zooms */}
      <div className="icon">
        <Help ariaDescribedBy={id} />
      </div>
    </button>
  );
}

HelpTooltip.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** ID for accessibility */
  id: PropTypes.string.isRequired,
  /** Optional title for the tooltip */
  title: PropTypes.string,
  /** Optional description for the tooltip */
  description: PropTypes.string,
  /** Optional JSX children e.g. if wanting to add lists and other formatted content. */
  children: PropTypes.element,
  /** Background color e.g. 'taos-sky', 'traffic-cone', 'kermit', 'white' */
  color: PropTypes.string,
  /** Optional text for the help link */
  helpLinkText: PropTypes.string,
  /** Optional URL for the link, required to show the link */
  helpLinkUrl: PropTypes.string,
  /** Size for the icon in rems, default 0.625rem */
  iconSize: PropTypes.number,
  /** Icon color e.g. 'taos-sky' and 'silver-sand', defaulting to color prop value if not set */
  iconColor: PropTypes.string,
  /** Width of the tooltip in rems */
  width: PropTypes.number,
  /** Handle when the tooltip is visible */
  onTooltipVisible: PropTypes.func,
  /** Handle clicks for the help link */
  onTooltipLinkClick: PropTypes.func,
  /** Show tooltip above or below icon */
  position: PropTypes.oneOf(['top', 'bottom']),
  /** Show new branding styles */
  isRebrand: PropTypes.bool,
};

HelpTooltip.defaultProps = {
  color: 'taos-sky',
  description: '',
  children: null,
  helpLinkText: 'Learn more',
  helpLinkUrl: '',
  iconSize: 0.625,
  iconColor: '',
  onTooltipVisible: () => {},
  onTooltipLinkClick: () => {},
  title: '',
  width: 12.63,
  position: 'top',
  isRebrand: false,
};

const styledHelpTooltip = styled(HelpTooltip)`
  ${(props) => (props.isRebrand ? lightInter() : book())};
  position: relative;
  padding: 0;
  margin: 0;
  background: none;
  border: 0;
  width: ${(props) => `${props.iconSize}rem`};
  height: ${(props) => `${props.iconSize}rem`};

  /* calling <Help /> puts a div around that thing, so this changes it to a div we can control */
  .icon {
    width: ${(props) => `${props.iconSize}rem`};
    height: ${(props) => `${props.iconSize}rem`};
  }

  svg {
    cursor: help;
    height: ${(props) => `${props.iconSize}rem`};

    path {
      fill: ${(props) => (props.iconColor ? nameToHex(props.iconColor) : nameToHex(props.color))};
    }
  }

  .tooltip {
    transition: 0.3s all ease;
    transition-delay: 0.4s;
    visibility: hidden;
    opacity: 0;
    position: absolute;
    z-index: 2000;

    h1 {
      ${(props) => (props.isRebrand ? semiBold() : heavy())};
      font-size: 0.6875rem;
      color: ${colors.white};
      padding: 0;
      margin: 0 0 0.375rem 0;
    }

    p {
      line-height: 0.875rem;
      margin: 0;
      padding: 0;
    }

    a.help {
      ${(props) => (props.isRebrand ? mediumInter() : medium())};
      display: block;
      position: relative;
      text-align: right;
      color: ${colors.white};
      font-size: 0.625rem;
    }
  }

  .tooltip {
    left: ${(props) => `calc(-${props.width / 2}rem + ${props.iconSize / 2}rem) `};
    background-color: ${(props) => nameToHex(props.color)};
    box-shadow: 0 0.1875rem 0.375rem ${colors.boxShadow};
    color: ${colors.white};
    font-size: 0.6875rem;
    text-align: left;
    width: ${(props) => props.width}rem;
    border-radius: 0.125rem;
  }
  .tooltip::after {
    position: absolute;
    left: ${(props) => props.width / 2}rem;
    margin-left: -0.3125rem;
    width: 0;
    content: ' ';
    font-size: 0;
    line-height: 0;
  }

  .tooltip.tooltip--top {
    /* 8px is the height of the triangle hack + 3 magic pixels. */
    bottom: ${(props) => `calc(0.5rem + ${props.iconSize}rem) `};
    padding: 0.75rem 0.625rem 0.625rem 0.875rem;
  }
  /* Triangle hack to make tooltip look like a speech bubble */
  .tooltip.tooltip--top::after {
    bottom: -0.3125rem;
    border-top: 0.3125rem solid ${(props) => nameToHex(props.color)};
    border-right: 0.3125rem solid transparent;
    border-left: 0.3125rem solid transparent;
  }

  .tooltip.tooltip--bottom {
    /* 8px is the height of the triangle hack + 3 magic pixels. */
    top: ${(props) => `calc(0.5rem + ${props.iconSize}rem) `};
    padding: 0.625rem 0.625rem 0.75rem 0.875rem;
    box-shadow: 0 -0.025rem 0.375rem ${colors.boxShadow};
  }
  /* Triangle hack to make tooltip look like a speech bubble */
  .tooltip.tooltip--bottom::after {
    top: -0.3125rem;
    border-bottom: 0.3125rem solid ${(props) => nameToHex(props.color)};
    border-right: 0.3125rem solid transparent;
    border-left: 0.3125rem solid transparent;
  }

  .tooltip--white {
    color: ${colors.boulder};

    h1 {
      color: ${(props) => (props.isRebrand ? colors.electricBlue : colors.taosSky)};
    }

    a.help {
      color: ${(props) => (props.isRebrand ? colors.electricBlue : colors.taosSky)};
    }
  }

  .tooltip.tooltip--visible {
    visibility: visible;
    opacity: 1;
    transition: 0.25s;
    transition-delay: 0;
  }
`;

export default styledHelpTooltip;
